import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  acceptInvitation,
  acceptInvitationMultiOrg,
  Api,
  getInvitationByToken,
  getOrganizations,
  getReceivedInvitations,
  InvitationResponseDto,
  requestCollaborationCancellation,
  withdrawCollaborationCancellation,
} from '@taxora-api';
import {
  AcceptInvitation,
  AcceptInvitationById,
  LoadInvitations,
  LoadInvitationByToken,
  RequestCollaborationCancellation,
  WithdrawCollaborationCancellation,
} from './invitations.actions';

export type ProviderRelation = {
  id: number;
  providerId: number;
  clientId: number;
  createdAt: string;
  status: string;
  endedAt?: string | null;
  cancellationRequestedAt?: string | null;
  cancellationRejectedMessage?: string | null;
  provider?: { id: number; name: string; cui?: number };
};

type OrganizationSummary = {
  id: number;
  name: string;
  cui?: number;
  count?: { relationships: number };
  providers?: ProviderRelation[];
};

interface InvitationsStateModel {
  invitation: InvitationResponseDto | null;
  pendingInvitations: InvitationResponseDto[];
  invitationHistory: InvitationResponseDto[];
  organizations: OrganizationSummary[];
  isLoading: boolean;
  isAccepting: boolean;
  isLoadingPage: boolean;
  hasError: boolean;
  isRequestingCancellation: boolean;
}

@State<InvitationsStateModel>({
  name: 'invitations',
  defaults: {
    invitation: null,
    pendingInvitations: [],
    invitationHistory: [],
    organizations: [],
    isLoading: false,
    isAccepting: false,
    isLoadingPage: false,
    hasError: false,
    isRequestingCancellation: false,
  },
})
@Injectable()
export class InvitationsState {
  private readonly api = inject(Api);

  @Selector()
  static invitation(state: InvitationsStateModel): InvitationResponseDto | null {
    return state.invitation;
  }

  @Selector()
  static pendingInvitations(state: InvitationsStateModel): InvitationResponseDto[] {
    return state.pendingInvitations;
  }

  @Selector()
  static invitationHistory(state: InvitationsStateModel): InvitationResponseDto[] {
    return state.invitationHistory;
  }

  @Selector()
  static organizations(state: InvitationsStateModel): OrganizationSummary[] {
    return state.organizations;
  }

  @Selector()
  static isLoading(state: InvitationsStateModel): boolean {
    return state.isLoading;
  }

  @Selector()
  static isAccepting(state: InvitationsStateModel): boolean {
    return state.isAccepting;
  }

  @Selector()
  static isLoadingPage(state: InvitationsStateModel): boolean {
    return state.isLoadingPage;
  }

  @Selector()
  static hasError(state: InvitationsStateModel): boolean {
    return state.hasError;
  }

  @Selector()
  static isRequestingCancellation(state: InvitationsStateModel): boolean {
    return state.isRequestingCancellation;
  }

  @Action(LoadInvitationByToken)
  async loadByToken(
    { patchState }: StateContext<InvitationsStateModel>,
    { token }: LoadInvitationByToken,
  ): Promise<void> {
    patchState({ isLoading: true, hasError: false, invitation: null });
    try {
      const invitation = await this.api.invoke(getInvitationByToken, { token });
      patchState({ invitation });
    } catch (error) {
      console.error('Failed to load invitation', error);
      patchState({ hasError: true });
    } finally {
      patchState({ isLoading: false });
    }
  }

  @Action(LoadInvitations)
  async loadInvitations({ patchState }: StateContext<InvitationsStateModel>): Promise<void> {
    patchState({ isLoadingPage: true });
    try {
      const [all, orgs] = await Promise.all([
        this.api.invoke(getReceivedInvitations, { limit: 100 }),
        this.api.invoke(getOrganizations, { member: 'me', fields: 'id,name,cui,providers,count' }),
      ]);
      patchState({
        pendingInvitations: all.data.filter((inv) => inv.status === 'PENDING'),
        invitationHistory: all.data.filter((inv) => inv.status !== 'PENDING'),
        organizations: orgs as OrganizationSummary[],
      });
    } catch (error) {
      console.error('Failed to load invitations page', error);
    } finally {
      patchState({ isLoadingPage: false });
    }
  }

  @Action(AcceptInvitation)
  async accept(
    { patchState }: StateContext<InvitationsStateModel>,
    { token, organizationIds }: AcceptInvitation,
  ): Promise<void> {
    patchState({ isAccepting: true });
    try {
      await this.api.invoke(acceptInvitationMultiOrg, { body: { token, organizationIds } });
    } catch (error) {
      console.error('Failed to accept invitation', error);
      throw error;
    } finally {
      patchState({ isAccepting: false });
    }
  }

  @Action(AcceptInvitationById)
  async acceptById(
    { patchState, dispatch }: StateContext<InvitationsStateModel>,
    { invitationId, organizationIds }: AcceptInvitationById,
  ): Promise<void> {
    patchState({ isAccepting: true });
    try {
      await Promise.all(
        organizationIds.map((orgId) =>
          this.api.invoke(acceptInvitation, { id: invitationId, body: { organizationId: orgId } }),
        ),
      );
      await dispatch(new LoadInvitations()).toPromise();
    } catch (error) {
      console.error('Failed to accept invitation by id', error);
      throw error;
    } finally {
      patchState({ isAccepting: false });
    }
  }

  @Action(RequestCollaborationCancellation)
  async requestCancellation(
    { patchState, dispatch }: StateContext<InvitationsStateModel>,
    { relationshipId, message }: RequestCollaborationCancellation,
  ): Promise<void> {
    patchState({ isRequestingCancellation: true });
    try {
      await this.api.invoke(requestCollaborationCancellation, {
        id: relationshipId,
        body: { message },
      });
      await dispatch(new LoadInvitations()).toPromise();
    } catch (error) {
      console.error('Failed to request collaboration cancellation', error);
      throw error;
    } finally {
      patchState({ isRequestingCancellation: false });
    }
  }

  @Action(WithdrawCollaborationCancellation)
  async withdrawCancellation(
    { patchState, dispatch }: StateContext<InvitationsStateModel>,
    { relationshipId }: WithdrawCollaborationCancellation,
  ): Promise<void> {
    patchState({ isRequestingCancellation: true });
    try {
      await this.api.invoke(withdrawCollaborationCancellation, { id: relationshipId });
      await dispatch(new LoadInvitations()).toPromise();
    } catch (error) {
      console.error('Failed to withdraw collaboration cancellation', error);
      throw error;
    } finally {
      patchState({ isRequestingCancellation: false });
    }
  }
}
